import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { QrCode, Eye, MessageSquare, Award, UserCheck, ExternalLink, TrendingUp, IndianRupee } from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ icon: Icon, n, l, accent }) => (
  <div className={`zq-card p-5 flex items-center gap-4 ${accent ? "border-zq-orange border-2" : ""}`}>
    <div className="w-10 h-10 rounded-md bg-zq-orange-light text-zq-orange flex items-center justify-center"><Icon size={20}/></div>
    <div>
      <div className="text-2xl font-bold">{n}</div>
      <div className="text-sm text-zq-muted">{l}</div>
    </div>
  </div>
);

export const RestaurantOverview = () => {
  const { restaurant } = useAuth();
  const [stats, setStats] = useState({ qr_scans: 0, menu_views: 0, feedback: 0, loyalty: 0, revenue: 0, commission_saved: 0, total_orders: 0 });

  useEffect(() => {
    Promise.all([
      api.get("/restaurant/feedback"),
      api.get("/restaurant/loyalty"),
      api.get("/restaurant/analytics"),
    ]).then(([f, l, a]) => {
      setStats({
        qr_scans: a.data.qr_scans,
        menu_views: a.data.menu_views,
        feedback: f.data.length,
        loyalty: l.data.length,
        revenue: a.data.revenue?.total_revenue || 0,
        commission_saved: a.data.revenue?.commission_saved || 0,
        total_orders: a.data.revenue?.total_orders || 0,
      });
    }).catch(() => {});
  }, []);

  const url = `${window.location.origin}/m/${restaurant?.slug}`;

  return (
    <div data-testid="rest-overview">
      <h1 className="text-2xl font-bold">Hello, {restaurant?.name}</h1>
      <p className="text-sm text-zq-muted mt-1">Here's a quick look at your restaurant.</p>

      {/* Commission saved hero card */}
      <div className="zq-card mt-6 p-6 bg-gradient-to-br from-orange-50 to-transparent border-zq-orange/30" data-testid="commission-saved-card">
        <div className="flex items-center gap-2">
          <TrendingUp className="text-zq-orange" size={20} />
          <span className="text-xs font-semibold uppercase tracking-wider text-zq-orange">Commission saved with ZaykaQR</span>
        </div>
        <div className="flex items-baseline gap-3 mt-2">
          <IndianRupee className="text-zq-charcoal" size={28} />
          <span className="text-4xl font-bold">{inr(stats.commission_saved).replace("₹", "")}</span>
          <span className="zq-pill zq-pill-orange">vs aggregators</span>
        </div>
        <p className="text-sm text-zq-muted mt-2">
          Based on {stats.total_orders} WhatsApp order{stats.total_orders === 1 ? "" : "s"} totalling {inr(stats.revenue)}.
          We assume a typical 25% aggregator commission you would otherwise pay.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
        <Stat icon={QrCode} n={stats.qr_scans} l="QR scans"/>
        <Stat icon={Eye} n={stats.menu_views} l="Menu views"/>
        <Stat icon={MessageSquare} n={stats.feedback} l="Feedback received"/>
        <Stat icon={Award} n={stats.loyalty} l="Loyalty members"/>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-6">
        <div className="zq-card p-6">
          <h3 className="font-semibold">Your menu URL</h3>
          <p className="text-xs text-zq-muted mt-1">Print this link inside your QR or share directly.</p>
          <div className="flex items-center gap-2 mt-3">
            <input className="zq-input flex-1 text-xs" readOnly value={url} data-testid="menu-url"/>
            <Link to="/dashboard/qr" className="zq-btn zq-btn-primary"><QrCode size={16}/></Link>
            <a href={url} target="_blank" rel="noreferrer" className="zq-btn zq-btn-outline"><ExternalLink size={16}/></a>
          </div>
        </div>
        <div className="zq-card p-6">
          <div className="flex items-center gap-2"><UserCheck className="text-zq-orange"/><h3 className="font-semibold">Account Manager</h3></div>
          <p className="mt-3 text-sm">{restaurant?.account_manager_name || "Will be assigned shortly"}</p>
          <p className="text-xs text-zq-muted mt-1">{restaurant?.account_manager_contact || "support@zaykaqr.space"}</p>
        </div>
      </div>
    </div>
  );
};
